import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { getApiBaseUrl } from "../config";
import {
  Eye,
  EyeOff,
  AlertCircle,
  Mail,
  LockKeyhole,
  LogIn,
  Check,
} from "lucide-react";
import Logo from "../components/Logo";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const [loginIdentifier, setLoginIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await login(loginIdentifier, password);
      navigate("/dashboard");
    } catch (err) {
      if (err.response?.data?.detail) {
        setError(formatApiError(err.response.data.detail));
      } else if (err.response?.status) {
        setError(`Server error (${err.response.status}). Please try again.`);
      } else if (
        err.code === "ERR_NETWORK" ||
        err.message === "Network Error" ||
        !err.response
      ) {
        const base = getApiBaseUrl();
        setError(
          `Unable to reach the API at ${base}. ` +
            "After changing frontend/.env, restart the dev server; production builds need REACT_APP_BACKEND_URL set when you run npm run build. " +
            `Open ${base}/api/health in this browser. If that fails, fix the API or network. ` +
            "If health works but login does not, check CORS (UI origin in CORS_ORIGINS) and COOKIE_SECURE / COOKIE_SAMESITE on the API (see root .env.example).",
        );
      } else {
        setError(err.message || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };
  // TODO: Add a loading state
  return (
    <div
      className="relative min-h-screen overflow-hidden bg-[#081126] text-white"
      data-testid="login-page"
      style={{
        backgroundImage: "url('/hppy_bg.png')",
        backgroundPosition: "center",
        backgroundSize: "cover",
      }}
    >
      <div className="absolute inset-y-0 left-0 hidden w-[48%] bg-[linear-gradient(90deg,rgba(22,33,79,0.52)_0%,rgba(21,34,84,0.58)_100%)] lg:block" />
      <div
        className="absolute inset-y-0 right-0 w-full lg:w-[52%]"
        style={{
          background:
            "linear-gradient(165deg, rgba(0, 0, 0, 0.45) 0%, rgba(0, 0, 0, 0.52) 100%)",
        }}
      />

      <div className="relative z-10 grid min-h-screen lg:grid-cols-[1.02fr_1fr]">
        <section className="hidden items-center justify-center px-16 lg:flex xl:px-24">
          <div className="max-w-[560px]">
            <Logo className="mb-10 h-20 w-auto" mode="dark" />

            <div className="mb-7 inline-flex rounded border border-white/20 bg-[#0A1124]/35 px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em] text-white shadow-sm backdrop-blur-sm">
              AI-powered revenue cycle intelligence
            </div>

            <h1 className="font-outfit text-4xl font-bold leading-[1.12] tracking-tight text-white xl:text-[42px]">
              Intelligent Generic Intelligence for{" "}
              <span className="text-[#F0B33F]">Healthcare Procurement</span>
            </h1>

            <p className="mt-7 max-w-[500px] text-base leading-7 text-white/75">
              Identify cost-saving opportunities, validate evidence, and
              streamline formulary decisions with confidence.
            </p>

            <div className="mt-8 space-y-5 text-sm text-white/80">
              {[
                "Evidence-based substitution analysis",
                "Geographic market intelligence",
                "Savings simulation & forecasting",
              ].map((feature) => (
                <div key={feature} className="flex items-center gap-4">
                  <span className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/10 shadow-[0_0_20px_rgba(240,179,63,0.15)]">
                    <Check className="h-4 w-4 text-[#F0B33F]" />
                  </span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="flex min-h-screen items-center justify-center px-6 py-10 sm:px-10 lg:px-16">
          <div className="w-full max-w-[460px] rounded-[20px] border border-[#B98533]/80 bg-[#0A1022]/75 p-9 shadow-[0_30px_90px_rgba(0,0,0,0.45)] backdrop-blur-md sm:p-11">
            <div className="mb-9 flex justify-center lg:hidden">
              <Logo className="h-16 w-auto" mode="dark" />
            </div>

            <h2 className="font-outfit text-3xl font-bold tracking-tight text-white">
              Welcome back
            </h2>
            <p className="mt-3 text-base text-white/60">
              Sign in to access your intelligence dashboard
            </p>

            {error && (
              <div
                className="mt-6 flex items-start gap-3 rounded-lg border border-red-400/30 bg-red-500/10 p-4"
                data-testid="login-error"
              >
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-300" />
                <p className="text-sm leading-5 text-red-100">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="mt-8 space-y-6">
              <div>
                <label className="mb-2.5 block text-sm font-medium text-white/75">
                  Email or username
                </label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40" />
                  <input
                    type="text"
                    autoComplete="username"
                    value={loginIdentifier}
                    onChange={(e) => setLoginIdentifier(e.target.value)}
                    className="w-full rounded-lg border border-white/25 bg-[#0A1022]/70 py-4 pl-12 pr-4 text-base text-white outline-none transition placeholder:text-white/40 focus:border-[#F0B33F] focus:ring-2 focus:ring-[#F0B33F]/20"
                    placeholder="you@hospital.org or jane_doe"
                    required
                    data-testid="login-email-input"
                  />
                </div>
              </div>

              <div>
                <label className="mb-2.5 block text-sm font-medium text-white/75">
                  Password
                </label>
                <div className="relative">
                  <LockKeyhole className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-white/40" />
                  <input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full rounded-lg border border-white/25 bg-[#0A1022]/70 py-4 pl-12 pr-12 text-base text-white outline-none transition placeholder:text-white/40 focus:border-[#F0B33F] focus:ring-2 focus:ring-[#F0B33F]/20"
                    placeholder="Enter your password"
                    required
                    data-testid="login-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-white/50 transition hover:text-white"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="font-outfit flex w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#E4A928] to-[#F2BE46] px-5 py-4 text-base font-semibold text-[#1B1730] shadow-[0_14px_30px_rgba(240,179,63,0.18)] transition hover:from-[#F0B33F] hover:to-[#F7CA62] disabled:cursor-not-allowed disabled:opacity-60"
                data-testid="login-submit-btn"
              >
                <LogIn className="h-5 w-5" />
                {loading ? "Signing in..." : "Sign in"}
              </button>
            </form>

            <p className="mt-8 text-center text-base text-white/60">
              Don't have an account?{" "}
              <Link
                to="/register"
                className="font-medium text-[#F0B33F] transition hover:text-[#F7CA62]"
              >
                Request access
              </Link>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
